import {html} from 'lit-html';
import {ifDefined} from 'lit-html/directives/if-defined';
import {live} from 'lit-html/directives/live';
import {classMap} from 'lit-html/directives/class-map';

import TemplateRegistry from '../template/template-registry';
import Translate from '../translate';
import * as log from 'loglevel';
import {PriceType} from '../const';
import {
    optionClassMap, optionName, optionDebounce
} from '../template/template-util.js';

class DimensionController {

    constructor(onChange) {
        this.onChange = onChange;
        this.x = 0;
        this.y = 0;
        this.area = 0;
        this.xInput = ''; // strings for UI
        this.yInput = '';
        this.xEl = null;
        this.yEl = null;
    }

    onValidate(option) {

        // custom validation                // NEW: check controller input values (value can be 0x0)   NEW:  minY = 0 exclude check (e.g dimension input ft/in with 0)
        if(option.required && (!option.value || this.x === 0 || (!(option.params.onlyX || option.params.minY === 0) && this.y === 0) )){
            option.errors.push(Translate.get('product.options.validity.valueMissing'));
            return false;
        }

        // min/max validation   // NEW: only validate if not empty (the required check does already check that it has to be entered)
        this.validateDimension(option, option.errors, !this.xInput, !this.yInput);

        if(option.errors.length){
            return false;
        }

        return true;
    }

    validateDimension(option, errors, xEmpty = false, yEmpty = false) {
        let x = this.x;
        let y = this.y;

        // switch to always make x the bigger side
        if(option.params.bigX && (x < y)){
            let temp = x;
            x = y;
            y = temp;
        }

        // INFO: we translate with dynamic keys (no # prefix) - key can be already translated text (fallback)
        const stripKey = (key) => key && key[0] === '#' ? key.substring(1) : key;

        // xEmpty flag to exclude min validation if not yet a value filled in
        if(!xEmpty && option.params.minX && x < option.params.minX){
            errors.push(Translate.get(stripKey(option.params.minXKey) || '{label} must be at least {min} {addon}', { label: option.params.labelX, min: option.params.minX, addon: option.params.addonX }));
        }
        if(!yEmpty && option.params.minY && y < option.params.minY){
            errors.push(Translate.get(stripKey(option.params.minYKey) || '{label} must be at least {min} {addon}', { label: option.params.labelY, min: option.params.minY, addon: option.params.addonY }));
        }
        if(option.params.maxX && x > option.params.maxX){
            errors.push(Translate.get(stripKey(option.params.maxXKey) || '{label} must be at most {max} {addon}', { label: option.params.labelX, max: option.params.maxX, addon: option.params.addonX }));
        }
        if(option.params.maxY && y > option.params.maxY){
            errors.push(Translate.get(stripKey(option.params.maxYKey) || '{label} must be at most {max} {addon}', { label: option.params.labelY, max: option.params.maxY, addon: option.params.addonY }));
        }
    }

    optionValue(option) {
        // combined option value text
        return (option.params.valueTemplate || '{x}x{y}').replace(new RegExp('{x}', 'g'), this.x).replace(new RegExp('{y}', 'g'), this.y).replace(new RegExp('{area}', 'g'), this.area.toFixed(2));
    }

    handleChange(e, option, restore = false) { // IMPORTANT: to pass option and not use the reference of the ['checkbox'] = (option, ..)   in editorMode the option reference can change

        // exclude restore (values set from option.value)
        if(!restore){
            // input with the combined values  ONLY lookup once (conditions restore the same element)
            this.xEl = this.xEl || document.querySelector('#' + option.id + '-x');
            this.yEl = this.yEl || document.querySelector('#' + option.id + '-y');

            // NEW: locked ratio
            if(option.params.ratio != null){
                // calculate opposite side based on ratio (simulated as if the other side was entered)
                if(this.xEl.value !== this.xInput){
                    this.yEl.value = parseFloat(this.xEl.value / option.params.ratio).toFixed(2);
                } else {
                    this.xEl.value = parseFloat(this.yEl.value * option.params.ratio).toFixed(2);
                }
            }

            // read input values back
            this.xInput = this.xEl.value;
            this.yInput = this.yEl.value;
        }

        // replace german comma with dots  (on blur the inputs will be overwritten with '.')
        const replaceComma = (value) => value ? value.replaceAll(',', '.') : '0';

        this.x = parseFloat(replaceComma(this.xInput)) || 0;
        this.y = parseFloat(replaceComma(this.yInput)) || 0;

        // step based rounding increases (feature flag)
        if(option.params.steps){ // check for positive number
            let step = option.params.steps;
            if(this.x % step !== 0) {
                this.x = Math.round(this.x / step) * step;
            }
            if(this.y % step !== 0) {
                this.y = Math.round(this.y / step) * step;
            }
            // force rounding to get UI update logic with blur support
            if(isNaN(option.params.rounding) || option.params.rounding === null){
                option.params.rounding = 0;
            }
        }

        // round to x number of decimals & update input
        if(!isNaN(option.params.rounding) && option.params.rounding !== null){

            // only update rounded inputs on init
            let updateInputs = (e == null);

            if(this.x !== 0){
                let xFixed = this.x.toFixed(option.params.rounding);
                this.x = parseFloat(xFixed);

                if(updateInputs){
                    this.xInput = xFixed;
                } else {
                    this.xInputBlur = () => {
                        this.xInput = xFixed;
                        this.xEl.value = this.xInput; // manually update UI field
                        delete this.xInputBlur;
                    };
                    // NEW: manual blur event handling (without triggering onChange again) - REQUIRED to avoid liveCrop crop button event not firing on the blur event > onChange
                    this.xEl.addEventListener('blur', (e) => {
                        this.xInputBlur ? this.xInputBlur() : null;
                    }, { once: true });
                }
            }
            if(this.y !== 0){
                let yFixed = this.y.toFixed(option.params.rounding);
                this.y = parseFloat(yFixed);

                if(updateInputs){
                    this.yInput = yFixed;
                } else {
                    this.yInputBlur = () => {
                        this.yInput = yFixed;
                        this.yEl.value = this.yInput; // manually update UI field
                        delete this.yInputBlur;
                    };
                    // NEW: manual blur event handling (without triggering onChange again) - REQUIRED to avoid liveCrop crop button event not firing on the blur event > onChange
                    this.yEl.addEventListener('blur', (e) => {
                        this.yInputBlur ? this.yInputBlur() : null;
                    }, { once: true });
                }
            }

            // HACK: blur event is not ALWAYS executed (seems to be a lit-html problem - different event listeners did not fix it)
            // we check every 500ms if focus is still set and manually execute blur event if needed
            if(!this.blurInterval && (this.xInputBlur || this.yInputBlur)){
                this.blurInterval = setInterval(() => {
                    if(this.xInputBlur){
                        if(document.activeElement !== this.xEl){
                            this.xInputBlur();
                        }
                    }
                    if(this.yInputBlur){
                        if(document.activeElement !== this.yEl){
                            this.yInputBlur();
                        }
                    }
                    // clear if no more pending blur events
                    if(!(this.xInputBlur || this.yInputBlur)){
                        clearInterval(this.blurInterval);
                        delete this.blurInterval;
                    }

                }, 500);
            }
        }

        // optional > area calc (required for custom input units - and can be added to the value and used in priceFormula)
        if(option.params.areaCalc){
            try {
                // cached function
                if(!option._areaFunction) {
                    try {
                        // INFO: we always wrap in return to only allow expressions
                        option._areaFunction = Function('"use strict";return (function(x,y){ return (' + option.params.areaCalc + ') })')();
                    } catch (error){
                        log.error('failed to parse areaCalc: ', option.params.areaCalc, error);
                        if(option.config && option.config.editorMode){
                            option.errors = option.errors || [];
                            option.errors.push(error);
                        }
                    }
                }

                if(option._areaFunction){
                    this.area = option._areaFunction(this.x, this.y); // x/y variable passed

                    // no result set to 0
                    if(!this.area){
                        this.area = 0;
                    }
                }

            } catch (error){
                log.error('failed to calculate area with areaCalc: ', option.params.areaCalc, error);
                if(option.config && option.config.editorMode){
                    option.errors = option.errors || [];
                    option.errors.push(error);
                }
            }
        } else {
            // default no need to execute a formula  (no rounding to keep precision when used in priceFormula)
            this.area = this.x * this.y;
        }

        // always execute validations
        let errors = [];
        this.validateDimension(option, errors, !this.xInput, !this.yInput);
        if(errors.length){
            option.errors = errors;
            option._error = true; // flag to avoid error rest on change
        }

        // combined option value text
        option.value = this.optionValue(option);

        if(!option.values){ // values required to select the optionValue with the priceFormula
            option.values = [];
        }

        let priceFormulaList = '';

        // NEW: linked price list lookup support
        if(option.list){
            priceFormulaList = 'var lookup=function(x,y,t){return options.lookup(option._controller.list(option), x,y,t)};';
            // auto generate price formula if just a list is linked
            if(!option.params.priceFormula && option.values.length){
                // check if nested values (Price table x,y lookup)
                if(option.values[0].values){
                    option.params.priceFormula = `lookup(x, y)`; // returned value is the final price
                }
                // default (Price list area lookup)
                else {
                    option.params.priceFormula = `lookup(area) * area`; // returned value is the square price
                }
            }
        }

        // optional > calculate price with formula
        if(option.params.priceFormula){

            // check if optionValue already setup
            if(option.values.length && option.values[0]._calc){
                // only update value
                option.values[0].value = option.value;
            } else {

                // INFO: we insert the controller variables to be used in the function (allows to cache the priceFunction)
                let priceFormula = 'var x=option._controller.x;var y=option._controller.y;var area=option._controller.area;' + priceFormulaList;
                // expression only - always wrap in return (requires to define variables before)
                if(option.params.priceFormula.indexOf('return') === -1){
                    priceFormula += 'return (' + option.params.priceFormula + ')';
                } else {
                    priceFormula += option.params.priceFormula;
                }

                // create a optionValue with a price formula
                let calcOptionsValue = { value: option.value, text: '', priceType: PriceType.FORMULA, priceFormula: priceFormula, '_calc': true }; // calc flag to update same optionValue

                option.values.unshift(calcOptionsValue);
            }
        } else {
            // default value when no price formula (required for the label selected optionValue)
            option.values.unshift({ value: option.value, text: '' });
        }

        // trigger change
        this.onChange(option);
    }

    list(option) { // used in lookup() priceFormula function
        // return option.values without the first entry (contains price formula)
        return option.values.slice(1);
    }

    restore(option) {
        // detect value change from outside (cart restore)
        if (option.value && option.value !== this.optionValue(option)) {
            // INFO: the hidden properties _x _y are not mapped back from the cart item
            // we restore by parsing the value template via regex
            let templatePattern = (option.params.valueTemplate || '{x}x{y}')
                // escape regex chars
                .replace('.', '\\.').replace('*', '\\*')
                // replace placeholder with regex capturing groups (?<> = named group)
                .replace(new RegExp('{x}', 'g'), '(?<x>.*)').replace(new RegExp('{y}', 'g'), '(?<y>.*)').replace(new RegExp('{area}', 'g'), '(?<area>.*)');

            let templateRegex = new RegExp(templatePattern, 'g');
            let match = templateRegex.exec(option.value);
            if(match && match.groups){
                if(match.groups.x !== undefined){
                    this.xInput = match.groups.x;
                }
                if(match.groups.y !== undefined){
                    this.yInput = match.groups.y;
                }
                // trigger on change to rebuild option value & price
                this.handleChange(null, option, true);

                return true; // restored
            }
        }
        return false;
    }

    init(option, productForm) {
        // isNaN returns does not handle null..
        if(!isNaN(option.params.valueX) && option.params.valueX !== null){
            this.xInput = String(option.params.valueX);
        }
        if(!isNaN(option.params.valueY) && option.params.valueY !== null){
            this.yInput = String(option.params.valueY);
        }

        // NEW HACKY: restore support on INIT - required when combined with personalize option and restore from cart
        if(option.value && this.restore(option)){
            return; // on change already triggered
        }

        if(this.xInput || this.yInput){
            // trigger on change to build option value & price
            this.handleChange(null, option, true);
        }
    }

    stepDec(option, dimension) {
        let value = this[dimension];
        let step = (option.params.numberStep || 1);
        // decimals based on configured rounding or default without rounding
        let decimals = !isNaN(option.params.rounding) && option.params.rounding !== null ? option.params.rounding : null;

        // increment rounding (step based)
        let steps = Math.round(value / step);
        // UPDATE: required to avoid floating point issues
        const tolerance = Math.pow(10, -10);
        const isExactStep = Math.abs(value - (steps * step)) < tolerance;

        if (isExactStep) {
            value -= step; // edge case - already divisible by step
        } else {
            value = Math.floor(value / step) * step;
        }

        // handle min value
        value = Math.max(value, option.params['min' + dimension.toUpperCase()] || 0);

        let el = this[dimension + 'El'];
        if(!el){ // edge case - when step is triggered before first change
            el = document.querySelector('#' + option.id + '-' + dimension);
        }
        // update inputEl value (read again on change) - required to support ratio logic
        el.value = decimals !== null ? value.toFixed(decimals) : value.toString();
        // trigger on change to build option value & price
        this.handleChange(null, option);
    }

    stepInc(option, dimension) {
        let value = this[dimension];
        let step = (option.params.numberStep || 1);
        // decimals based on configured rounding or default without rounding
        let decimals = !isNaN(option.params.rounding) && option.params.rounding !== null ? option.params.rounding : null;

        // increment rounding (step based)
        let steps = Math.round(value / step);
        // UPDATE: required to avoid floating point issues
        const tolerance = Math.pow(10, -10);
        const isExactStep = Math.abs(value - (steps * step)) < tolerance;

        // increment rounding (step based)
        if (isExactStep) {
            value += step; // edge case - already divisible by step
        } else {
            value = Math.ceil(value / step) * step;
        }

        // handle max value
        value = Math.min(value, option.params['max' + dimension.toUpperCase()] || Number.MAX_SAFE_INTEGER);

        let el = this[dimension + 'El'];
        if(!el){ // edge case - when step is triggered before first change
            el = document.querySelector('#' + option.id + '-' + dimension);
        }
        // update inputEl value (read again on change) - required to support ratio logic
        el.value = decimals !== null ? value.toFixed(decimals) : value.toString();
        // trigger on change to build option value & price
        this.handleChange(null, option);
    }
}

TemplateRegistry.templates['dimension'] = (option, config, onChange) => {

    // init flag when no controller yet
    let init = option._controller === undefined;

    // get or create multi select controller
    option._controller = option._controller || new DimensionController(onChange);

    if(init){
        // IMPORTANT: init after first render -> (because init can trigger onChange - required to support init of all following options)
        const initController = () => {
            // remove init event
            config.productForm.removeEventListener('options:rendered', initController);
            // call init
            option._controller.init(option);
        };
        // wait for options render to complete ( could be solved better by support custom javascript post render execution by option ( maybe define on _controller.postRender() )
        config.productForm.addEventListener('options:rendered', initController);

    } else {
        // (cart restore) INFO: we call restore until xEl is set (first user change)
        if(option.value && !option._controller.xEl){
            option._controller.restore(option);
        } else if(!option.value && option._controller.xEl){
            // value cleared from outside (productOptionsClear) > reset inputs to match
            Object.assign(option._controller, { xInput: '', yInput: '', x: 0, y: 0, area: 0 });
        }
    }

    const xInput =
        html`<input type="text" class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}-x" name="properties[_${option.name}_x]" 
                .value=${live(option._controller.xInput)} @input=${optionDebounce(option, (e) => option._controller.handleChange(e, option))} 
                ?required=${option.required} form=${ifDefined(config.productFormLink)}>`;

    const yInput =
        html`<input type="text" class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}-y" name="properties[_${option.name}_y]" 
                .value=${live(option._controller.yInput)} @input=${optionDebounce(option, (e) => option._controller.handleChange(e, option))}
                ?required=${option.required && !option.params.onlyX} form=${ifDefined(config.productFormLink)}>`;

    return html`
            <input type="hidden" name="${optionName(option)}" id="${option.id}" value=${option.value} form=${ifDefined(config.productFormLink)} />
            <div class="cl-po--dimension-container">
                 <div class="cl-po--dimension-item ${classMap({ 'full': option.params.onlyX })}">
                     ${option.params.labelX ? html`<label class="cl-po--label" for="${option.id}-x">${option.params.labelX}</label>` : null }
                     <div class="cl-po--input-group ${classMap({'cl-po--dimension-steps': option.params.numberSteps, 'cl-po--addon': option.params.addonX })}">
                         ${option.params.numberSteps ? 
                         html`<div class="cl-po--quantity-wrap">
                                <span class="cl-po--quantity-btn" @click=${(e) => { option._controller.stepDec(option, 'x') }}>-</span>
                                ${xInput}
                                <span class="cl-po--quantity-btn" @click=${(e) => { option._controller.stepInc(option, 'x') }}>+</span>
                            </div>` 
                         : xInput}
                         ${option.params.addonX ? html`<span class="cl-po--input cl-po--input-addon">${option.params.addonX}</span>` : null }
                     </div>
                 </div>
                <div class="cl-po--dimension-item ${classMap({ 'cl-hide': option.params.onlyX })}">
                    ${option.params.labelY ? html`<label class="cl-po--label" for="${option.id}-y">${option.params.labelY}</label>` : null }
                    <div class="cl-po--input-group ${classMap({'cl-po--dimension-steps': option.params.numberSteps, 'cl-po--addon': option.params.addonY })}">
                        ${option.params.numberSteps ?
                        html`<div class="cl-po--quantity-wrap">
                                <span class="cl-po--quantity-btn" @click=${(e) => { option._controller.stepDec(option, 'y') }}>-</span>
                                ${yInput}
                                <span class="cl-po--quantity-btn" @click=${(e) => { option._controller.stepInc(option, 'y') }}>+</span>
                            </div>`
                          : yInput}
                        ${option.params.addonY ? html`<span class="cl-po--input cl-po--input-addon">${option.params.addonY}</span>` : null }
                    </div>
                </div>
           </div>
        `;
};